import type { CustomFieldDependents } from "@repo/app-types/client/client";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInsertClient,
  CustomFieldFilterValuesResponse,
  CustomFieldResourceType,
} from "@repo/app-types/entities/customField";
import type { PlaybookScope } from "@repo/app-types/entities/filters";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const userCustomFieldApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // User custom field definitions endpoints
    allUserCustomFieldDefinitions: builder.query<CustomFieldDefinition[], void>(
      {
        query: () => `/custom_fields/user-custom-field-definitions`,
        providesTags: (result) =>
          getTagsProvidedByList(TagType.UserCustomFieldDefinition, result),
      },
    ),

    customFieldFilterValues: builder.query<
      CustomFieldFilterValuesResponse,
      { customFieldDefinitionId: string; scope: PlaybookScope; query?: string }
    >({
      query: ({ customFieldDefinitionId, scope, query }) => ({
        url: `/custom_fields/${encodeURIComponent(customFieldDefinitionId)}/filtered-values`,
        params: {
          playbookId: scope.playbookId,
          after: scope.dateRange.after,
          before: scope.dateRange.before,
          dateBasis: scope.dateBasis,
          timeZone: scope.timeZone,
          query,
        },
      }),
      transformResponse: (response: {
        data: CustomFieldFilterValuesResponse;
      }) => response.data,
    }),

    createUserCustomFieldDefinition: builder.mutation<
      CustomFieldDefinition,
      Omit<
        CustomFieldDefinitionInsertClient,
        "provider" | "field_name" | "resource_type"
      > & {
        field_name?: string;
        resource_type:
          | typeof CustomFieldResourceType.CONTACT
          | typeof CustomFieldResourceType.INTERVIEW
          | typeof CustomFieldResourceType.GRADING;
      }
    >({
      query: (values) => ({
        method: "POST",
        url: `/custom_fields/user-custom-field-definitions`,
        body: values,
      }),
      invalidatesTags: () =>
        getTagsInvalidatedByCreate(TagType.UserCustomFieldDefinition),
    }),

    userCustomFieldDependents: builder.query<
      CustomFieldDependents,
      string,
      { data: { custom_field_dependents: CustomFieldDependents } }
    >({
      query: (id) =>
        `/custom_fields/user-custom-field-definitions/${encodeURIComponent(id)}/dependents`,
      transformResponse: (response) => response.data.custom_field_dependents,
    }),

    deleteUserCustomFieldDefinition: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/custom_fields/user-custom-field-definitions/${encodeURIComponent(id)}`,
      }),
      // Optimistic updates on delete
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          userCustomFieldApi.util.updateQueryData(
            "allUserCustomFieldDefinitions",
            undefined,
            (draft) => {
              const index = draft.findIndex((field) => field.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.UserCustomFieldDefinition, id),
    }),
  }),
});

export const {
  useAllUserCustomFieldDefinitionsQuery,
  useCustomFieldFilterValuesQuery,
  useCreateUserCustomFieldDefinitionMutation,
  useUserCustomFieldDependentsQuery,
  useDeleteUserCustomFieldDefinitionMutation,
} = userCustomFieldApi;
